import React, { useEffect, useState } from "react";
import Select from "react-select";
import "../styles/CandidateCreate.scss";
import { Country, State, City } from "country-state-city";
import {
  getCandidateInfo,
  updateCandidate,
  getAllSkills,
  base_URL,
} from "../services/api";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

function EditCandidate() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    jobTitle: "",
    experience: "",
    location: "",
    present_ctc: "",
    expected_cdc: "",
    notice_period: "",
    status: "New",
    skills: [],
    resume: null,
    linkedin: "",
    portfolio: "",
    notes: "",
  });
  const [resumeName, setResumeName] = useState("");
  const [error, setError] = useState("");
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();

  // Function to get Indian cities
  const getIndianCities = () => {
    try {
      // Get India country code
      const india = Country.getCountryByCode('IN');
      if (!india) return [];

      // Get all states of India
      const indianStates = State.getStatesOfCountry('IN');
      if (!indianStates || indianStates.length === 0) return [];

      // Get cities from all states
      const allCities = [];
      indianStates.forEach(state => {
        const cities = City.getCitiesOfState('IN', state.isoCode);
        if (cities && cities.length > 0) {
          cities.forEach(city => {
            allCities.push({
              value: city.name,
              label: `${city.name}`,
            });
          });
        }
      });

      // Sort cities alphabetically
      return allCities.sort((a, b) => a.label.localeCompare(b.label));
    } catch (error) {
      console.error("Error fetching Indian cities:", error);
      return [];
    }
  };

  // Fetch skills from API on component mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        Swal.fire({
          html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });
        const response = await getAllSkills();
        Swal.close();
        if (
          response &&
          response.result_code === 200 &&
          Array.isArray(response.result_info)
        ) {
          const skills = response.result_info.map((skill) => ({
            value: skill.skill_name || skill.name || skill,
            label: skill.skill_name || skill.name || skill,
          }));
          setSkillsOptions(skills);
        }
      } catch (error) {
        console.error("Error fetching skills:", error);
      }
    };

    // Fetch Indian cities
    const indianCities = getIndianCities();
    setLocationOptions(indianCities);

    fetchSkills();
  }, []);

  // Fetch candidate info from API
  useEffect(() => {
    if (id) {
      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });
      getCandidateInfo(id).then((res) => {
        Swal.close();
        if (res && res.status === "S" && res.result_info) {
          const c = res.result_info;
          setForm({
            fullName: c.candidate_name || "",
            email: c.email || "",
            phone: c.phone || "",
            jobTitle: c.job_title || "",
            experience: c.years_of_exp !== null && c.years_of_exp !== undefined ? String(c.years_of_exp) : "",
            location: c.location || "",
            present_ctc: c.present_ctc || "",
            expected_cdc: c.expected_cdc || "",
            notice_period: c.notice_period || "",
            status: c.status || "New",
            skills: c.skills ? c.skills.split(/,\s*/) : [],
            resume: null,
            linkedin: c.linkedin_url || "",
            portfolio: c.portfolio || "",
            notes: c.additional_notes || "",
            resumeUrl: c.resume_path
              ? `${
                  process.env.REACT_APP_API_URL || base_URL
                }files/resume/${c.resume_path.replace(/\\/g, "/")}`
              : "",
          });
          setResumeName("");
        }
      });
    }
  }, [id]);

  const handleFileChange = (e) => {
  const { name, files } = e.target;
  if (files && files[0]) {
    const file = files[0];
    const maxSize = 10 * 1024 * 1024; // 10 MB
    const allowedFormats = ['pdf', 'doc', 'docx'];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    // Validate file format
    if (!allowedFormats.includes(fileExtension)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid File Format",
        text: "Only PDF, DOC, and DOCX files are allowed.",
        confirmButtonColor: "#0078b9",
      });
      e.target.value = "";
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: "Please upload a file smaller than 10 MB.",
        confirmButtonColor: "#0078b9",
      });
      e.target.value = "";
      return;
    }

    // If valid, update state
    setForm((f) => ({ ...f, [name]: file }));
    if (name === "resume") setResumeName(file.name);
  }
};

  const isInValidEmail = (value) => {
    const emailPattern = /^[a-zA-Z0-9]+([._+-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
    // Check if the value matches email pattern
    if (emailPattern.test(value)) {
      return false;
    } else {
      return true;
    }
  };
  //For candidate update integration
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation checks with SweetAlert
    if (!form.fullName.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Full Name",
        text: "Please enter the candidate's full name.",
      });
      return;
    }
    if (!form.email.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Email",
        text: "Please enter the candidate's email address.",
      });
      return;
    }
    if (isInValidEmail(form.email)) {
      Swal.fire({
        icon: 'warning',
        title: "Invalid Email",
        text: 'Please enter valid email.',
      });
      return;
    }
    if (!form.phone.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Phone",
        text: "Please enter the candidate's phone number.",
      });
      return;
    }
    if (form.phone.length < 10) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone Number",
        text: "Please enter a valid phone number.",
      });
      return;
    }
    if (
      !form.experience
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Experience",
        text: "Please enter years of experience.",
      });
      return;
    }

    if (!form.present_ctc || isNaN(Number(form.present_ctc)) || Number(form.present_ctc) <= 0) {
          Swal.fire({
            icon: "warning",
            title: "Missing Present CTC",
            text: "Please enter a valid Present CTC.",
          });
          return;
        }
    
        // Check if expected CTC exists and is valid  
        if (!form.expected_cdc || isNaN(Number(form.expected_cdc)) || Number(form.expected_cdc) <= 0) {
          Swal.fire({
            icon: "warning",
            title: "Missing Expected CTC",
            text: "Please enter a valid Expected CTC.",
          });
          return;
        }
    
        // Compare the values
        const presentCTC = Number(form.present_ctc);
        const expectedCTC = Number(form.expected_cdc);
    
        if (expectedCTC <= presentCTC) {
          Swal.fire({
            icon: "warning",
            title: "Invalid CTC",
            text: `Expected CTC must be greater than Present CTC`,
          });
          return;
        }
    

    if (!form.skills || form.skills.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Skills",
        text: "Please select at least one skill.",
      });
      return;
    }
    if (!form.resume && !form.resumeUrl) {
      Swal.fire({
        icon: "warning",
        title: "Missing Resume",
        text: "Please upload the candidate's resume.",
      });
      return;
    }
    const createdBy = localStorage.getItem("created_by") || "1";
    const formData = new FormData();
    formData.append("candidate_name", form.fullName.trim());
    formData.append("email", form.email.trim().toLowerCase());
    formData.append("phone", form.phone.trim());
    formData.append("years_of_exp", form.experience.endsWith('-') ? form.experience.slice(0, -1) : form.experience);
    formData.append("linkedin_url", form.linkedin.trim());
    formData.append("location", form.location.trim());
    formData.append("present_ctc", form.present_ctc);
    formData.append("expected_cdc", form.expected_cdc);
    formData.append("notice_period", form.notice_period);
    formData.append("skills", form.skills.join(", "));
    formData.append("additional_notes", form.notes.trim());
    formData.append("created_by", createdBy);
    if (form.resume) formData.append("resume", form.resume);
    // Always send status and jobTitle if present
    if (form.status) formData.append("status", form.status);
    if (form.jobTitle) formData.append("job_title", form.jobTitle);
    try {
      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });

      const res = await updateCandidate(id, formData);
      // Swal.close();
      if (res && res.status === "S") {
        Swal.fire({
          icon: "success",
          title: "Candidate updated successfully!",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          navigate("/candidates");
        });
      } else {
        setError(res?.message || "Failed to update user. Please try again.");
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.message || "Failed to update user. Please try again.",
        });
      }
    } catch (err) {
      setError(err?.message || "Failed to update user. Please try again.");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Failed to update user. Please try again.",
      });
    }
  };

  return (
    <div className="candidates-page">
      <div className="candidate-create-container">
        <h2 style={{ fontWeight: 600, fontSize: "1.3rem" }}>
          Edit Candidate Profile
        </h2>
        <div className="candidate-create-form">
          {/* Candidate Details */}
          <div className="candidate-section">
            <div className="candidate-section-title">Candidate Details</div>
            <div className="candidate-row">
              <div className="candidate-field">
                <label>Full Name *</label>
                <input
                  name="fullName"
                  value={form.fullName}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    // Don't allow if it doesn't start with a letter (but allow empty string)
                    if (value !== "" && !/^[a-zA-Z]/.test(value)) {
                      return; // Reject if doesn't start with letter
                    }
                    // Prevent consecutive spaces
                    if (/\s{2,}/.test(value)) {
                      return; // Reject if two or more spaces occur consecutively
                    }
                    const invalidPattern =
                      /<script.*?>.*?<\/script>|[<>\\{0-9}=:/""'']/i;
                    if (!invalidPattern.test(value)) {
                      setForm({ ...form, fullName: value });
                    }
                  }}
                              
                  autoComplete="off"
                  required
                  placeholder="Enter full name"
                  className="custom-placeholder"
                />
              </div>
              <div className="candidate-field">
                <label>Email *</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    // Don't allow if it doesn't start with a letter (but allow empty string)
                    if (value !== "" && !/^[a-zA-Z]/.test(value)) {
                      return; // Reject if doesn't start with letter
                    }
                    const emailPattern =
                      /^[a-zA-Z0-9._%+-]+(@[a-zA-Z0-9.-]*)?$/;
                    if (value && emailPattern.test(value)) {
                      setForm({ ...form, email: value });
                    } else if (!value) {
                      setForm({ ...form, email: "" });
                    }
                  }}
                              
                  autoComplete="off"
                  required
                  placeholder="Enter email address"
                  className="custom-placeholder"
                />
              </div>
            </div>
            <div className="candidate-row">
              <div className="candidate-field">
                <label>Phone Number *</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    
                    // Allow only digits and limit to 10 characters max
                    const onlyDigits = value.replace(/\D/g, ''); // Remove everything except digits

                    if (/^\+?[0-9 ]*$/.test(value) && onlyDigits.length <= 14) {
                      setForm({ ...form, phone: value });
                    }
                  }}
                  maxLength={10}
                  minLength={10}
                  autoComplete="off"
                  required
                  placeholder="Enter phone number"
                  className="custom-placeholder"
                />
              </div>
              <div className="candidate-field">
                <label>Years of Experience *</label>
                <input
                  name="experience"
                  type="text"
                  min="0"
                  value={form.experience}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const digitOnlyPattern = /^\d*\.?\d*$/; // Allow any number of digits
                    if (digitOnlyPattern.test(value)) {
                      setForm({ ...form, experience: value });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  // maxLength={255}
                  autoComplete="off"
                  placeholder="Enter years of experience"
                  className="custom-placeholder"
                />
              </div>
            </div>
            <div className="candidate-row">
              <div className="candidate-field">
                <label>LinkedIn URL</label>
                <input
                  name="linkedin"
                  value={form.linkedin}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const invalidPattern =
                      /<script.*?>.*?<\/script>|[<>\\{}=""'']/i;
                    if (!invalidPattern.test(value)) {
                      setForm({ ...form, linkedin: value });
                    }
                  }}
                              
                  autoComplete="off"
                  placeholder="https://www.linkedin.com/in/your-profile"
                  className="custom-placeholder"
                />
              </div>
              <div className="candidate-field">
                <label>Preferred Location</label>
                <Select
                  name="location"
                  options={locationOptions}
                  value={
                    form.location
                      ? locationOptions.find(
                          (opt) => opt.value === form.location
                        )
                      : null
                  }
                  onChange={(selected) =>
                    setForm((f) => ({
                      ...f,
                      location: selected ? selected.value : "",
                    }))
                  }
                  placeholder="Select Preferred Location"
                  isSearchable={true}
                  classNamePrefix="react-select"
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: 38,
                      borderRadius: 6,
                      fontSize: "1rem",
                      background: "#fff",
                      border: "1.5px solid #d1d5db",
                    }),
                    menu: (base) => ({ ...base, zIndex: 20 }),
                  }}
                />
              </div>
            </div>
            <div className="candidate-row">
              <div className="candidate-field">
                <label>Present CTC (in lakhs)*</label>
                <input
                  name="present_ctc"
                  type="text"
                  min="0"
                  value={form.present_ctc}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const digitOnlyPattern = /^\d*(\.\d*)?$/;
                    if (value !== "" && !/^[0-9]/.test(value)) {
                        return; // Reject if doesn't start with letter
                      }
                    if (digitOnlyPattern.test(value)) {
                      setForm({ ...form, present_ctc: value });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter present ctc"
                  className="custom-placeholder"
                  autoComplete="off"
                />
              </div>
              <div className="candidate-field">
                <label>Expected CTC (in lakhs)*</label>
                <input
                  name="expected_cdc"
                  type="text"
                  min="0"
                  value={form.expected_cdc}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const digitOnlyPattern = /^\d*(\.\d*)?$/;
                    if (value !== "" && !/^[0-9]/.test(value)) {
                        return; // Reject if doesn't start with letter
                      }
                    // const digitOnlyPattern = /^[0-9.]/; // Max 2 digits (e.g., 99 years)
                    if (digitOnlyPattern.test(value)) {
                      setForm({ ...form, expected_cdc: value });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter expected ctc"
                  className="custom-placeholder"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="candidate-row">
              <div className="candidate-field">
                <label>Notice Period</label>
                <input
                  name="notice_period"
                  type="number"
                  min="0"
                  value={form.notice_period}
                  onChange={(ev) => {
                    const value = ev.target.value;

                    const isValid = value === '' || /^\d[A-Za-z0-9. ]*$/.test(value);

                    if (isValid) {
                      setForm({ ...form, notice_period: value });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  // maxLength={255}
                  autoComplete="off"
                  placeholder="Enter notice period"
                  className="custom-placeholder"
                />
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="candidate-section">
            <div className="candidate-section-title">Skills *</div>
            <Select
              isMulti
              options={skillsOptions}
              value={form.skills
                .map((skill) =>
                  skillsOptions.find((opt) => opt.value === skill)
                )
                .filter(Boolean)}
              onChange={(selected) =>
                setForm((f) => ({
                  ...f,
                  skills: selected ? selected.map((opt) => opt.value) : [],
                }))
              }
              placeholder="Select skills"
              isSearchable={true}
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 38,
                  borderRadius: 6,
                  fontSize: "1rem",
                  background: "#fff",
                  border: "1.5px solid #d1d5db",
                  marginBottom: "1.2rem",
                }),
                menu: (base) => ({ ...base, zIndex: 20 }),
              }}
            />
          </div>

          {/* Documents & Profile */}
          <div className="candidate-section">
            <div className="candidate-section-title">Documents</div>
            <div className="candidate-row">
              <div className="candidate-field" style={{ flex: 2 }}>
                <label
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  Upload Resume *
                  {form.resumeUrl && (
                    <a
                      href={form.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0078b9",
                        textDecoration: "underline",
                        fontWeight: 500,
                        fontSize: "1rem",
                        marginLeft: "auto",
                      }}
                    >
                      View Existing Resume
                    </a>
                  )}
                </label>
                <div className="file-upload-box">
                  <input
                    type="file"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    id="resume-upload"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="resume-upload" className="file-upload-label">
                    <span className="file-upload-icon">&#8682;</span>
                    {resumeName ? (
                      resumeName
                    ) : (
                      <span>
                        Drop resume here or{" "}
                        <span className="file-upload-link">browse</span>
                      </span>
                    )}
                    <div className="file-upload-desc">
                      PDF, DOC, DOCX up to 10MB
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
          {/* Additional Notes */}
          <div className="candidate-section">
            <div className="candidate-section-title">Additional Notes</div>
            <textarea
              className="candidate-notes custom-placeholder"
              name="notes"
              value={form.notes}
              onChange={(ev) => {
                const value = ev.target.value;
                const invalidPattern =
                  /<script.*?>.*?<\/script>|[<>\\{}=:/""'']/i;
                if (!invalidPattern.test(value)) {
                  setForm({ ...form, notes: value });
                }
              }}
                          
              placeholder="Add any additional remarks or notes about the candidate"
              rows="2"
              autoComplete="off"
            />
          </div>
          {error && (
            <div style={{ color: "#e53e3e", marginBottom: 12 }}>{error}</div>
          )}
          <div className="candidate-actions">
            <button
              className="cancel-btn"
              type="button"
              onClick={() => navigate("/candidates")}
            >
              Cancel
            </button>
            <button className="save-btn" onClick={handleSubmit}>
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditCandidate;
